import BuildIcon from "@mui/icons-material/Build";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import GamepadIcon from "@mui/icons-material/Gamepad";
import InfoIcon from "@mui/icons-material/Info";
import PauseIcon from "@mui/icons-material/Pause";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WifiIcon from "@mui/icons-material/Wifi";
import {
    Button,
    IconButton,
    Popover,
    Slider,
    Tooltip,
    styled,
} from "@mui/material";
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
    buildFileBufferObject,
    FileBufferObject,
} from "../components/bufferFileUpload";
import {
    FileUploadButton,
    openFileDialog,
} from "../components/fileUploadButton";
import GamePad from "../components/gamePad";
import GameScreen from "../components/gameScreen";
import { HiddenInput } from "../components/inputStyles";
import { MenuButton } from "../components/menuButton";
import { GameBoyMode, ModeSwitch } from "../components/modeSwitch";
import SiteFooter from "../components/siteFooter";
import type { EspLoadState } from "../espLoadLogic";
import {
    useResponsiveBreakpoint,
    ResponsiveBreakpoint,
} from "../hooks/useResponsiveBreakpoint";

// ── GB body chrome ──────────────────────────────────────────────────────────

const PageWrapper = styled("div")`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #111;
    padding: 24px 12px;
    box-sizing: border-box;

    @media (max-width: 767px) {
        padding: 12px 8px;
        align-items: flex-start;
    }
`;

const GameBoyBody = styled("div")`
    background: linear-gradient(165deg, #636363 0%, #505050 100%);
    border-radius: 24px;
    padding: 16px 18px 24px;
    width: 100%;
    max-width: 440px;
    zoom: 1.5;
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.14),
        inset 0 -2px 0 rgba(0, 0, 0, 0.35),
        0 20px 60px rgba(0, 0, 0, 0.85),
        0 4px 16px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (max-width: 767px) {
        zoom: 1;
        max-width: 100%;
    }
`;

// ── Top bar ──────────────────────────────────────────────────────────────────

const TopBar = styled("div")`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2px;
`;

const PowerRow = styled("div")`
    display: flex; /* Restore power indicator */
    align-items: center;
    gap: 6px;
`;

const PowerLED = styled("div")<{ $active?: boolean }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $active }) => ($active ? "#66bb6a" : "rgba(0,0,0,0.3)")};
    box-shadow: ${({ $active }) =>
        $active ? "0 0 8px #66bb6a, 0 0 3px rgba(102, 187, 106, 0.6)" : "none"};
    flex-shrink: 0;
`;

const PowerLabel = styled("span")`
    color: rgba(255, 255, 255, 0.35);
    font-size: 9px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
`;

const TopButtonGroup = styled("div")`
    display: flex;
    gap: 2px;
`;

const GBIconMenuButton = styled(Button)`
    color: rgba(255, 255, 255, 0.75);
    min-width: 0;
    padding: 6px 8px;
    border-radius: 8px;
    font-size: 0px; /* hide text label */
    &:hover {
        color: white;
        background: rgba(255, 255, 255, 0.1);
    }
    & .MuiButton-startIcon {
        margin: 0;
    }
`;

// ── Screen section ───────────────────────────────────────────────────────────

const ScreenSection = styled("div")`
    background: #232323;
    border-radius: 12px;
    padding: 18px 18px 14px;
    box-shadow:
        inset 0 4px 14px rgba(0, 0, 0, 0.95),
        inset 0 0 0 1px rgba(0, 0, 0, 0.55),
        0 1px 0 rgba(255, 255, 255, 0.06);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

const ScreenHingeRow = styled("div")`
    display: flex;
    gap: 10px;
    align-self: flex-start;
    margin-bottom: -6px;
`;

const ScreenHingeBar = styled("div")`
    height: 4px;
    width: 36px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.1);
`;

const ScreenLip = styled("div")`
    background: #090909;
    padding: 6px;
    border-radius: 4px;
    line-height: 0;
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.05),
        0 2px 10px rgba(0, 0, 0, 0.9);
`;

const LogoRow = styled("div")`
    display: flex;
    align-items: baseline;
    gap: 8px;
`;

const NanuLogoImg = styled("img")`
    height: 20px;
    opacity: 0.85;
`;

// ── Action section ───────────────────────────────────────────────────────────

const ActionSection = styled("div")`
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 8px 12px;
    align-items: center;
    padding: 0 2px;
`;

const ActionRow = styled("div")`
    display: contents; /* grid components parent */
`;

const FileStatus = styled("span")<{ $ready?: boolean }>`
    color: ${({ $ready }) =>
        $ready ? "rgba(102,187,106,0.9)" : "rgba(255,255,255,0.35)"};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    font-size: 18px;
`;

const GBPlayButton = styled(IconButton)`
    color: rgba(255, 255, 255, 0.85);
    padding: 6px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.25);
    &:hover {
        background: rgba(0, 0, 0, 0.45);
        color: white;
    }
    &:disabled {
        color: rgba(255, 255, 255, 0.2);
    }
`;

// ── Component ────────────────────────────────────────────────────────────────

const StandardView = ({
    gameKey,
    onOpenControls,
    onOpenCheats,
    onOpenLicenses,
    onInoFilesSelect,
    inoLoaded,
    espLoadState,
    playDisabled,
    playing,
    paused,
    mode,
    rom,
    volume,
    onPlay,
    onPause,
    onResume,
    onScreenshot,
    onModeChange,
    onRomChange,
    onRomLoadError,
    onVolumeChange,
    onLoadStateChange,
    onSaveState,
    onImportBackupChange,
    onExportBackup,
    canvasRef,
    loadStateRef,
    importBackupRef,
}: StandardViewProps): JSX.Element => {
    const inoInputRef = useRef<HTMLInputElement>(null);
    const breakpoint = useResponsiveBreakpoint();
    const isMobile = breakpoint === ResponsiveBreakpoint.xs;
    const isTablet = breakpoint === ResponsiveBreakpoint.sm;

    const gameBodyRef = useRef<HTMLDivElement>(null);
    const naturalDimRef = useRef<{ w: number; h: number } | null>(null);
    const [mobileZoom, setMobileZoom] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        const update = () => {
            const el = gameBodyRef.current;
            if (!el || window.innerWidth > 767) {
                naturalDimRef.current = null;
                setMobileZoom(undefined);
                return;
            }
            if (!naturalDimRef.current) {
                naturalDimRef.current = {
                    w: el.offsetWidth,
                    h: el.offsetHeight,
                };
            }
            const { w: natW, h: natH } = naturalDimRef.current;
            const availW = window.innerWidth - 16;
            const availH = window.innerHeight - 24;
            const zoom = Math.min(availW / natW, availH / natH, 1);
            setMobileZoom(zoom < 0.99 ? zoom : undefined);
        };
        const id = setTimeout(update, 0);
        window.addEventListener("resize", update);
        return () => {
            clearTimeout(id);
            window.removeEventListener("resize", update);
        };
    }, []);

    useEffect(() => {
        if (isMobile || isTablet) {
            document.documentElement.style.overflow = "hidden";
            document.body.style.overflow = "hidden";
            return () => {
                document.documentElement.style.overflow = "";
                document.body.style.overflow = "";
            };
        }
    }, [isMobile, isTablet]);

    const [volumeAnchorEl, setVolumeAnchorEl] =
        useState<HTMLButtonElement | null>(null);
    const volumeOpen = Boolean(volumeAnchorEl);

    return (
        <PageWrapper>
            {/* Hidden inputs */}
            <HiddenInput
                type="file"
                accept=".rbs"
                ref={loadStateRef}
                onChange={onLoadStateChange}
            />
            <HiddenInput
                type="file"
                accept=".json"
                ref={importBackupRef}
                onChange={onImportBackupChange}
            />
            <HiddenInput
                ref={inoInputRef}
                type="file"
                accept=".ino,.h,.elf"
                multiple
                onChange={e => {
                    const files = Array.from(e.target.files ?? []);
                    if (files.length > 0) void onInoFilesSelect(files);
                    e.target.value = "";
                }}
            />

            <GameBoyBody
                ref={gameBodyRef}
                style={
                    mobileZoom !== undefined ? { zoom: mobileZoom } : undefined
                }
            >
                {/* ── Top bar ── */}
                <TopBar>
                    <PowerRow>
                        <PowerLED $active={playing} />
                        <PowerLabel />
                    </PowerRow>
                    <TopButtonGroup>
                        <ModeSwitch
                            disabled={playing || paused}
                            mode={mode}
                            onModeChange={onModeChange}
                        />
                        <MenuButton
                            variant="text"
                            startIcon={<SettingsIcon />}
                            withMobileMenu={true}
                            mobileMenuTitle="Settings"
                            sx={{
                                color: "rgba(255,255,255,0.7)",
                                minWidth: 0,
                                padding: "6px 8px",
                                borderRadius: "8px",
                                fontSize: 0,
                                "& .MuiButton-startIcon": { margin: 0 },
                                "&:hover": {
                                    color: "white",
                                    background: "rgba(255,255,255,0.1)",
                                },
                            }}
                            menuItems={[
                                {
                                    display: "Controls",
                                    icon: <GamepadIcon fontSize="small" />,
                                    action: onOpenControls,
                                    key: "controls",
                                    visible: !isMobile && !isTablet,
                                },
                                {
                                    display: (
                                        <span>
                                            Load ESP8266{" "}
                                            {inoLoaded && (
                                                <span
                                                    style={{
                                                        color: "rgba(102,187,106,0.9)",
                                                        fontSize: "0.8em",
                                                    }}
                                                >
                                                    Ready
                                                </span>
                                            )}
                                        </span>
                                    ),
                                    icon: <WifiIcon fontSize="small" />,
                                    action: () => inoInputRef.current?.click(),
                                    key: "load-esp8266",
                                },
                                {
                                    display: "Import Backup",
                                    icon: <FileUploadIcon fontSize="small" />,
                                    action: () => {
                                        openFileDialog(importBackupRef);
                                    },
                                    key: "import-backup",
                                },
                                {
                                    display: "Export Backup",
                                    icon: <FileDownloadIcon fontSize="small" />,
                                    action: onExportBackup,
                                    key: "export-backup",
                                },
                                {
                                    display: "Licenses",
                                    icon: <InfoIcon fontSize="small" />,
                                    action: onOpenLicenses,
                                    key: "licenses",
                                },
                            ]}
                        >
                            {""}
                        </MenuButton>
                        <MenuButton
                            variant="text"
                            startIcon={<BuildIcon />}
                            disabled={!gameKey}
                            withMobileMenu={true}
                            mobileMenuTitle="Game Tools"
                            sx={{
                                color: "rgba(255,255,255,0.7)",
                                minWidth: 0,
                                padding: "6px 8px",
                                borderRadius: "8px",
                                fontSize: 0,
                                "& .MuiButton-startIcon": { margin: 0 },
                                "&:hover": {
                                    color: "white",
                                    background: "rgba(255,255,255,0.1)",
                                },
                                "&.Mui-disabled": {
                                    color: "rgba(255,255,255,0.2)",
                                },
                            }}
                            menuItems={[
                                {
                                    display: "Cheats",
                                    icon: (
                                        <VideogameAssetIcon fontSize="small" />
                                    ),
                                    action: onOpenCheats,
                                    key: "cheats",
                                },
                                {
                                    display: "Screenshot",
                                    icon: <PhotoCameraIcon fontSize="small" />,
                                    action: onScreenshot,
                                    key: "screenshot",
                                },
                                {
                                    display: "Load State",
                                    icon: <FileUploadIcon fontSize="small" />,
                                    action: () => {
                                        openFileDialog(loadStateRef);
                                    },
                                    key: "load-state",
                                },
                                {
                                    display: "Save State",
                                    icon: <FileDownloadIcon fontSize="small" />,
                                    action: onSaveState,
                                    key: "save-state",
                                },
                            ]}
                        >
                            {""}
                        </MenuButton>
                        <Tooltip title="Volume">
                            <IconButton
                                onClick={e =>
                                    setVolumeAnchorEl(e.currentTarget)
                                }
                                sx={{
                                    color: "rgba(255,255,255,0.7)",
                                    padding: "6px 8px",
                                    borderRadius: "8px",
                                    "&:hover": {
                                        color: "white",
                                        background: "rgba(255,255,255,0.1)",
                                    },
                                }}
                            >
                                {volume === 0 ? (
                                    <VolumeOffIcon sx={{ fontSize: 19 }} />
                                ) : (
                                    <VolumeUpIcon sx={{ fontSize: 19 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Popover
                            open={volumeOpen}
                            anchorEl={volumeAnchorEl}
                            onClose={() => setVolumeAnchorEl(null)}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        background: "#3a3a3a",
                                        padding: "12px 18px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    },
                                },
                            }}
                        >
                            <Slider
                                orientation="vertical"
                                size="small"
                                value={volume}
                                min={0}
                                max={1}
                                step={0.01}
                                onChange={(_, val) =>
                                    onVolumeChange(val as number)
                                }
                                sx={{
                                    height: 80,
                                    color: "rgba(255,255,255,0.7)",
                                    "& .MuiSlider-thumb": {
                                        width: 12,
                                        height: 12,
                                    },
                                    "& .MuiSlider-rail": { opacity: 0.3 },
                                }}
                            />
                        </Popover>
                    </TopButtonGroup>
                </TopBar>

                {/* ── Screen ── */}
                <ScreenSection>
                    <ScreenHingeRow>
                        <ScreenHingeBar />
                        <ScreenHingeBar />
                    </ScreenHingeRow>
                    <ScreenLip>
                        <GameScreen
                            playing={playing}
                            paused={paused}
                            fullscreen={false}
                            ref={canvasRef}
                        />
                    </ScreenLip>
                    <LogoRow>
                        <NanuLogoImg
                            src={`${process.env.PUBLIC_URL}/nanu-logo.svg`}
                            alt="nanu"
                        />
                    </LogoRow>
                </ScreenSection>

                {/* ── Action controls ── */}
                <ActionSection>
                    {/* ROM load */}
                    <ActionRow>
                        <FileUploadButton
                            variant="outlined"
                            accept=".gb,.gbc"
                            startIcon={
                                <FileUploadIcon
                                    sx={{ fontSize: "20px !important" }}
                                />
                            }
                            sx={{
                                borderRadius: "8px",
                                fontSize: "18px",
                                padding: "10px 12px",
                                color: "rgba(255,255,255,0.85)",
                                borderColor: "rgba(255,255,255,0.18)",
                                background: "rgba(0,0,0,0.22)",
                                minWidth: 0,
                                width: "100%",
                                "&:hover": {
                                    borderColor: "rgba(255,255,255,0.32)",
                                    background: "rgba(0,0,0,0.38)",
                                },
                                "& .MuiButton-startIcon": {
                                    marginRight: "8px",
                                },
                            }}
                            onFileSelect={async fileList => {
                                if (!fileList?.[0]) return;
                                try {
                                    const buf = await buildFileBufferObject(
                                        fileList[0],
                                    );
                                    onRomChange(buf);
                                } catch (error) {
                                    console.error("Failed to load ROM:", error);
                                    onRomLoadError(
                                        "Failed to load ROM. The file may be invalid or too large.",
                                    );
                                }
                            }}
                        >
                            Load ROM
                        </FileUploadButton>
                        <FileStatus $ready={!!rom && !playDisabled}>
                            {rom
                                ? `${rom.filename}${
                                      espLoadState === "loading"
                                          ? " · ESP loading…"
                                          : espLoadState === "error"
                                            ? " · ESP error"
                                            : ""
                                  }`
                                : "No file chosen"}
                        </FileStatus>
                        <Tooltip
                            title={
                                paused ? "Resume" : playing ? "Pause" : "Play"
                            }
                        >
                            <span>
                                <GBPlayButton
                                    disabled={
                                        !playing && !paused && playDisabled
                                    }
                                    onClick={
                                        playing
                                            ? onPause
                                            : paused
                                              ? onResume
                                              : onPlay
                                    }
                                >
                                    {playing ? (
                                        <PauseIcon fontSize="small" />
                                    ) : (
                                        <PlayArrowIcon fontSize="small" />
                                    )}
                                </GBPlayButton>
                            </span>
                        </Tooltip>
                    </ActionRow>
                </ActionSection>

                {/* ── Gamepad (mobile/tablet) ── */}
                {(isMobile || isTablet) && <GamePad playing={playing} />}

                <SiteFooter />
            </GameBoyBody>
        </PageWrapper>
    );
};

interface StandardViewProps {
    readonly gameKey: string | null;
    readonly playing: boolean;
    readonly paused: boolean;
    readonly mode: GameBoyMode;
    readonly rom: FileBufferObject | null;
    readonly volume: number;
    readonly onOpenControls: () => void;
    readonly onOpenCheats: () => void;
    readonly onOpenLicenses: () => void;
    readonly onInoFilesSelect: (files: File[]) => Promise<void>;
    readonly inoLoaded: boolean;
    readonly espLoadState: EspLoadState;
    readonly playDisabled: boolean;
    readonly onPlay: () => void;
    readonly onPause: () => void;
    readonly onResume: () => void;
    readonly onScreenshot: () => void;
    readonly onModeChange: (mode: GameBoyMode) => void;
    readonly onRomChange: (rom: FileBufferObject | null) => void;
    readonly onRomLoadError: (message: string) => void;
    readonly onVolumeChange: (volume: number) => void;
    readonly onLoadStateChange: (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => Promise<void>;
    readonly onSaveState: () => void;
    readonly onImportBackupChange: (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => Promise<void>;
    readonly onExportBackup: () => void;
    readonly canvasRef: RefObject<HTMLCanvasElement>;
    readonly loadStateRef: RefObject<HTMLInputElement>;
    readonly importBackupRef: RefObject<HTMLInputElement>;
}

export default StandardView;
